import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toBibTeX, toRIS, downloadText } from "@/lib/cite";
import { Loader2, ChevronRight, Download, Eye, Quote, Mail, FileText, Share2, Copy, FileDown, AlertTriangle } from "lucide-react";

export default function PaperDetail() {
  const { id } = useParams();
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    setLoading(true);
    api.get(`/papers/${id}`).then(({ data }) => setPaper(data)).finally(() => setLoading(false));
  }, [id]);

  const handleDownload = () => {
    const key = paper?.submission_id || id;
    window.open(`${BACKEND}/api/papers/file/${encodeURIComponent(key)}.pdf`, "_blank", "noopener");
  };

  const share = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast.success("Link copied to clipboard");
  };

  const trackCite = () => { api.post(`/papers/${id}/cite`).then(({ data }) => setPaper((p) => ({ ...p, citations: data.citations }))).catch(() => {}); };

  const copyBibtex = () => {
    navigator.clipboard?.writeText(toBibTeX(paper));
    trackCite();
    toast.success("BibTeX copied to clipboard");
  };
  const dlBibtex = () => {
    downloadText(`${paper.doi?.replace(/[^\w.-]/g, "_") || paper.id}.bib`, toBibTeX(paper));
    trackCite();
    toast.success("BibTeX file downloaded");
  };
  const dlRis = () => {
    downloadText(`${paper.doi?.replace(/[^\w.-]/g, "_") || paper.id}.ris`, toRIS(paper));
    trackCite();
    toast.success("RIS file downloaded");
  };

  if (loading) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!paper) return <div className="max-w-7xl mx-auto px-6 py-20">Paper not found.</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex items-center gap-2 text-sm text-slate-500 mb-6 flex-wrap">
        <Link to="/" className="hover:text-primary">Home</Link>
        <ChevronRight className="w-4 h-4" />
        {paper.journal && <Link to={`/journal/${paper.journal.slug}`} className="hover:text-primary">{paper.journal.title}</Link>}
      </div>

      <div className="grid lg:grid-cols-[1fr_300px] gap-10">
        <article data-testid="paper-detail">
          {paper.retracted && (
            <div className="mb-5 rounded-xl border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-4" data-testid="retraction-notice">
              <p className="font-bold text-red-700 dark:text-red-400 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> RETRACTED ARTICLE</p>
              {paper.retraction_note && <p className="text-sm text-red-700/90 dark:text-red-300/90 mt-1.5">{paper.retraction_note}</p>}
            </div>
          )}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300">{paper.article_type}</Badge>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">Open Access</Badge>
          </div>

          <h1 onClick={handleDownload} title="Open the full-text PDF" data-testid="paper-title-link" className="font-serif font-black text-3xl md:text-4xl leading-tight text-slate-900 dark:text-slate-100 cursor-pointer hover:text-primary dark:hover:text-sky-400 transition-colors">{paper.title}</h1>

          <div className="mt-5 space-y-1.5">
            {paper.authors?.map((a, i) => (
              <div key={i} className="text-sm">
                <span className="font-medium text-slate-900 dark:text-slate-200">{a.name}</span>
                {a.is_corresponding && <Mail className="inline w-3.5 h-3.5 ml-1 text-amber-600" />}
                {a.affiliation && <span className="text-slate-500"> — {a.affiliation}</span>}
              </div>
            ))}
          </div>

          {paper.published_at && (
            <p className="text-sm text-slate-500 mt-4">
              {paper.journal?.title}
              {paper.volume ? `, Vol. ${paper.volume} No. ${paper.issue}` : ""}
              {paper.pages ? `, pp. ${paper.pages}` : ""} · Published {new Date(paper.published_at).toLocaleDateString()}
            </p>
          )}
          {paper.doi && (
            <p className="font-mono text-sm mt-1">
              DOI: <button type="button" onClick={handleDownload} data-testid="doi-link" className="text-primary dark:text-sky-400 hover:underline">{paper.doi}</button>
            </p>
          )}

          <Separator className="my-8" />

          <h2 className="font-serif font-bold text-xl mb-3 text-slate-900 dark:text-slate-100">Abstract</h2>
          <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-justify" data-testid="paper-abstract">{paper.abstract}</p>

          {paper.keywords?.length > 0 && (
            <div className="mt-5 flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-slate-500">Keywords:</span>
              {paper.keywords.map((k) => <Badge key={k} variant="secondary" className="font-normal">{k}</Badge>)}
            </div>
          )}

          {paper.attachments?.length > 0 && (
            <>
              <Separator className="my-8" />
              <h2 className="font-serif font-bold text-xl mb-4 text-slate-900 dark:text-slate-100">Figures &amp; Supplementary Files</h2>
              <div className="space-y-6">
                {paper.attachments.map((at, i) => (
                  at.kind === "image" ? (
                    <figure key={i} data-testid={`figure-${i}`}>
                      <img src={`${BACKEND}${at.url}`} alt={at.name} className="rounded-lg border border-slate-200 dark:border-slate-800 max-w-full" />
                      <figcaption className="text-xs text-slate-500 mt-2">{at.name}</figcaption>
                    </figure>
                  ) : (
                    <a key={i} href={`${BACKEND}${at.url}`} target="_blank" rel="noreferrer" data-testid={`attachment-link-${i}`}
                      className="inline-flex items-center gap-2 text-primary dark:text-sky-400 hover:underline">
                      <FileDown className="w-4 h-4" /> {at.name}
                    </a>
                  )
                ))}
              </div>
            </>
          )}

          {paper.manuscript_text && (
            <>
              <Separator className="my-8" />
              <h2 className="font-serif font-bold text-xl mb-3 text-slate-900 dark:text-slate-100">Full Text</h2>
              <div className="text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line">{paper.manuscript_text}</div>
            </>
          )}
        </article>

        <aside className="space-y-5">
          <div className="lg:sticky lg:top-24 space-y-5">
            <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-5">
              <Button onClick={handleDownload} data-testid="download-pdf-btn" className="w-full rounded-full gap-2">
                <Download className="w-4 h-4" /> Download PDF
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="cite-btn" className="w-full rounded-full gap-2 mt-3">
                    <Quote className="w-4 h-4" /> Cite
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem onClick={copyBibtex} data-testid="cite-copy-bibtex"><Copy className="w-4 h-4 mr-2" /> Copy BibTeX</DropdownMenuItem>
                  <DropdownMenuItem onClick={dlBibtex} data-testid="cite-download-bibtex"><FileDown className="w-4 h-4 mr-2" /> Download .bib</DropdownMenuItem>
                  <DropdownMenuItem onClick={dlRis} data-testid="cite-download-ris"><FileDown className="w-4 h-4 mr-2" /> Download .ris (EndNote)</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={share} variant="outline" data-testid="share-btn" className="w-full rounded-full gap-2 mt-3">
                <Share2 className="w-4 h-4" /> Share
              </Button>
            </div>
            <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-5">
              <h4 className="font-sans font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Metrics</h4>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><Eye className="w-5 h-5 mx-auto text-slate-400 mb-1" /><div className="font-bold text-lg">{paper.views || 0}</div><div className="text-[11px] text-slate-500">Views</div></div>
                <div><Download className="w-5 h-5 mx-auto text-slate-400 mb-1" /><div className="font-bold text-lg">{paper.downloads || 0}</div><div className="text-[11px] text-slate-500">Downloads</div></div>
                <div><Quote className="w-5 h-5 mx-auto text-slate-400 mb-1" /><div className="font-bold text-lg">{paper.citations || 0}</div><div className="text-[11px] text-slate-500">Citations</div></div>
              </div>
            </div>
            {paper.journal && (
              <Link to={`/journal/${paper.journal.slug}`} className="block bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-5 hover:shadow-md transition-all">
                <FileText className="w-5 h-5 text-amber-600 mb-2" />
                <p className="text-xs text-slate-500 uppercase tracking-wider">Published in</p>
                <p className="font-serif font-bold text-slate-900 dark:text-slate-100 mt-1">{paper.journal.title}</p>
              </Link>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
